/**
 * Offline queue for the rep's store page (2026-10-16, Helena: "yes, good idea" — saving visits with no signal).
 *
 * Every save on My route (visit tick, store comment, article reason / comment, checklist tick, supplier photos,
 * Submit) goes through `send(path, body, files?)`:
 *   • sent straight away when the phone has signal;
 *   • when the request cannot reach the server (no response at all — no signal, timeout), it is KEPT ON THE PHONE
 *     (IndexedDB, photos as blobs) and sent later, in the order it was made — when the signal comes back
 *     (`online` event), every 30 s while the page is open, and when the page opens again.
 * A refusal from the server (4xx / 5xx with a reply) is NOT queued — it is shown, as before.
 * A visit tick carries `at` (when the rep ticked it) so a late send keeps the real time (fieldController.visit).
 *
 * Limits: the page itself must already be open (or in the browser's cache) — opening SpearBI for the first time
 * without signal still needs the network. The queue is per phone; signing out does not clear it.
 */
import axios from "axios"

const API = process.env.REACT_APP_API_URL
const DB_NAME = "spearbi-field"
const STORE = "outbox"

let dbp = null
const db = () => {
  if (dbp) return dbp
  dbp = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "key", autoIncrement: true }) }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    } catch (e) { reject(e) }
  }).catch((e) => { dbp = null; throw e })
  return dbp
}
const tx = async (mode, fn) => {
  const d = await db()
  return new Promise((resolve, reject) => {
    const t = d.transaction(STORE, mode)
    const out = fn(t.objectStore(STORE))
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out)
    t.onerror = () => reject(t.error)
  })
}
const all = () => tx("readonly", (s) => s.getAll())
const put = (item) => tx("readwrite", (s) => s.add(item))
const del = (key) => tx("readwrite", (s) => s.delete(key))

const listeners = new Set()
let count = 0
const notify = () => listeners.forEach((fn) => { try { fn(count) } catch (e) { /* noop */ } })
const refreshCount = async () => { try { count = (await all()).length } catch (e) { count = 0 } notify() }

// A request that never got a reply (no signal) — the only kind that is queued.
const unreachable = (e) => !e?.response

const post = (path, body, files) => {
  if (files && files.length) {
    const fd = new FormData()
    fd.append("payload", JSON.stringify(body))
    files.forEach((f, i) => fd.append("images", f.blob || f, f.name || `photo-${i + 1}.jpg`))
    return axios.post(`${API}/field/${path}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 10 * 60 * 1000 })
  }
  return axios.post(`${API}/field/${path}`, body, { timeout: 30 * 1000 })
}

// → {sent: true, data} | {queued: true}. Throws on a server refusal.
export const send = async (path, body, files = null) => {
  const pending = count > 0
  // Keep the order: while older items wait, a new one waits behind them.
  if (!pending && (typeof navigator === "undefined" || navigator.onLine !== false)) {
    try { const r = await post(path, body, files); return { sent: true, data: r.data } } catch (e) { if (!unreachable(e)) throw e }
  }
  try {
    await put({ path, body, files: files ? files.map((f) => ({ blob: f.blob || f, name: f.name || "photo.jpg" })) : null, made: new Date().toISOString() })
  } catch (e) {
    throw new Error("No signal, and this phone cannot keep the save — try again when you have signal")
  }
  await refreshCount()
  return { queued: true }
}

let flushing = false
// Sends what is waiting, oldest first; stops at the first one that still cannot get through.
export const flush = async () => {
  if (flushing) return
  flushing = true
  let sent = 0
  try {
    const items = (await all()).sort((a, b) => a.key - b.key)
    for (const it of items) {
      try { await post(it.path, it.body, it.files) } catch (e) {
        if (unreachable(e)) break
        console.log("outbox: dropped a save the server refused", it.path, e?.response?.data?.message) // eslint-disable-line no-console
      }
      await del(it.key)
      sent += 1
    }
  } catch (e) { /* IndexedDB unavailable */ } finally {
    flushing = false
    await refreshCount()
  }
  return sent
}

export const onCount = (fn) => { listeners.add(fn); fn(count); return () => listeners.delete(fn) }

let started = false
export const start = (onFlushed) => {
  refreshCount().then(() => count && flush().then((n) => n && onFlushed && onFlushed(n)))
  if (started) return
  started = true
  const go = () => flush().then((n) => n && onFlushed && onFlushed(n))
  window.addEventListener("online", go)
  setInterval(() => { if (count) go() }, 30 * 1000)
}
